// src/pages/partnerAnalysis.jsx
import React, { useState, useEffect, useMemo } from "react";
import { PiShoppingBag } from "react-icons/pi";
import { MdSupervisorAccount, MdAccessTime } from "react-icons/md";
import { FaRupeeSign } from "react-icons/fa";
import { PiHospitalThin } from "react-icons/pi";
import { GrLocation } from "react-icons/gr";
import {
    FiMaximize2,
    FiMinimize2,
    FiChevronDown,
    FiChevronUp,
} from "react-icons/fi";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { CiCalendar } from "react-icons/ci";
import moment from "moment";
import { getPartnerDetails } from "../../../../Service/partnerAPI";
import { partnerPortalImages } from "../../../../reusableUI/images";

const formatCurrency = (value) =>
    `₹${(Number(value) || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
    })}`;

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const m = moment(dateStr);
    if (!m.isValid()) return "-";
    return m.format("DD MMM YYYY");
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const m = moment(dateStr);
    if (!m.isValid()) return "-";
    return m.format("DD MMM YYYY, hh:mm A");
};

const getBookingStatusInfo = (rawStatus) => {
    const s = (rawStatus || "").toLowerCase();
    if (s === "endduty" || s === "completed") {
        return { label: "Completed", className: "bg-emerald-50 text-emerald-600" };
    }
    if (s === "pending" || s === "payment pending") {
        return { label: "Pending", className: "bg-amber-50 text-amber-600" };
    }
    if (s === "ongoing" || s === "startduty" || s === "started") {
        return { label: "On-going", className: "bg-blue-50 text-blue-600" };
    }
    return {
        label: rawStatus || "Unknown",
        className: "bg-slate-100 text-slate-600",
    };
};

const getTxnStatusLabel = (rawStatus) => {
    const s = (rawStatus || "").toLowerCase();
    if (s === "endduty" || s === "completed") return "Completed";
    if (s === "pending" || s === "payment pending") return "Pending";
    return rawStatus || "Unknown";
};

const getTxnTypeLabel = (rawStatus) => {
    const s = (rawStatus || "").toLowerCase();
    if (s === "endduty" || s === "completed") return "Credit";
    if (s === "pending" || s === "payment pending") return "Pending";
    return "Credit";
};

const StatCard = ({ label, value, icon, accentClasses }) => (
    <div className="bg-white rounded-[10px] shadow-sm border border-slate-100 px-4 py-3 flex flex-col justify-between">
        <div
            className={`w-10 h-10 mb-2 rounded-[13px] flex items-center justify-center text-[18px] font-semibold ${accentClasses}`}
        >
            {icon}
        </div>
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="mt-2 text-xl font-semibold text-slate-900">{value}</p>
    </div>
);

const OverviewCard = ({ title, children, right }) => (
    <div className="bg-white rounded-[10px] shadow-sm border border-slate-100 px-4 py-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-600">{title}</p>
            {right}
        </div>
        {children}
    </div>
);

const TabButton = ({ active, children, onClick }) => (
    <button
        onClick={onClick}
        className={[
            "px-3 py-1.5 rounded-full text-xs sm:text-sm transition-colors",
            active
                ? "bg-indigo-50 text-indigo-600 font-semibold"
                : "bg-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-700",
        ].join(" ")}
    >
        {children}
    </button>
);

const BookingStatusPill = ({ status }) => {
    const { label, className } = getBookingStatusInfo(status);
    return (
        <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${className}`}
        >
            {label}
        </span>
    );
};

const HcpStatusPill = ({ status }) => (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-600">
        <AiOutlineCheckCircle className="w-3 h-3" />
        {status}
    </span>
);

const TxnStatusPill = ({ status }) => {
    const isCompleted = status === "Completed";
    const styles = isCompleted
        ? "bg-emerald-50 text-emerald-600"
        : "bg-amber-50 text-amber-600";

    return (
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${styles}`}>
            {status}
        </span>
    );
};

const TxnTypePill = ({ type }) => {
    const isCredit = type === "Credit";
    const styles = isCredit
        ? "bg-indigo-50 text-indigo-600"
        : "bg-amber-50 text-amber-600";

    return (
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${styles}`}>
            {type}
        </span>
    );
};

const PartnerAnalysis = ({ setSelectedPartner, partnerId, coverImage, createdAt }) => {
    const [activeTab, setActiveTab] = useState("bookings");
    const [search, setSearch] = useState("");
    const [isExpanded, setIsExpanded] = useState(false);

    const [partnerInfo, setPartnerInfo] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [hcpList, setHcpList] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);

    const [sortBy, setSortBy] = useState("newest");
    const [expandedJobId, setExpandedJobId] = useState(null);

    useEffect(() => {
        if (!partnerId) return;
        getPartner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerId]);

    const getPartner = async () => {
        try {
            setLoading(true);
            const response = await getPartnerDetails(partnerId);
            console.log("getPartnerDetails response:", response);
            if (response?.error === false && response.data) {
                const d = response.data;
                setPartnerInfo(d.partnerInfo || null);
                setMetrics(d.metrics || null);
                setBookings(d.bookings || []);
                setHcpList(d.hcpList || []);
                setTransactions(d.transactions || []);
            } else {
                console.log("getPartnerDetails error:", response?.message);
            }
        } catch (e) {
            console.log("getPartnerDetails exception:", e);
        } finally {
            setLoading(false);
        }
    };

    const statCards = useMemo(() => {
        const m = metrics || {};
        return [
            {
                id: "totalHcps",
                label: "Total HCPs",
                value: m.totalUsers ?? 0,
                icon: <MdSupervisorAccount />,
                accentClasses: "bg-blue-50 text-blue-500",
            },
            {
                id: "activeHcps",
                label: "Active HCPs",
                value: m.activeHCP ?? 0,
                icon: <MdAccessTime />,
                accentClasses: "bg-green-50 text-green-500",
            },
            {
                id: "totalBookings",
                label: "Total Bookings",
                value: m.totalBooking ?? 0,
                icon: <PiShoppingBag />,
                accentClasses: "bg-blue-50 text-blue-500",
            },
            {
                id: "totalEarnings",
                label: "Total Earnings",
                value: formatCurrency(m.totalEarning),
                icon: <FaRupeeSign />,
                accentClasses: "bg-purple-50 text-purple-500",
            },
            {
                id: "pendingAmount",
                label: "Pending Amount",
                value: formatCurrency(m.pendingAmount),
                icon: <MdAccessTime />,
                accentClasses: "bg-orange-50 text-orange-500",
            },
        ];
    }, [metrics]);

    // ---- FILTER + SORT ----

    const filteredBookings = useMemo(() => {
        let list = bookings.filter((b) =>
            (b.patientName || "").toLowerCase().includes(search.toLowerCase())
        );

        list.sort((a, b) => {
            const ma = moment(a.date);
            const mb = moment(b.date);

            if (sortBy === "newest") return mb.valueOf() - ma.valueOf();
            if (sortBy === "oldest") return ma.valueOf() - mb.valueOf();
            if (sortBy === "atoz")
                return (a.patientName || "").localeCompare(b.patientName || "");
            if (sortBy === "ztoa")
                return (b.patientName || "").localeCompare(a.patientName || "");
            return 0;
        });

        return list;
    }, [bookings, search, sortBy]);

    const filteredHcps = useMemo(() => {
        let list = hcpList.filter((h) =>
            (h.name || "").toLowerCase().includes(search.toLowerCase())
        );

        list.sort((a, b) => {
            if (sortBy === "atoz")
                return (a.name || "").localeCompare(b.name || "");
            if (sortBy === "ztoa")
                return (b.name || "").localeCompare(a.name || "");
            if (sortBy === "newest")
                return (b.totalBookings || 0) - (a.totalBookings || 0);
            if (sortBy === "oldest")
                return (a.totalBookings || 0) - (b.totalBookings || 0);
            return 0;
        });

        return list;
    }, [hcpList, search, sortBy]);

    const filteredTxns = useMemo(() => {
        let list = transactions.filter((t) => {
            const jobId = (t.jobID || "").toLowerCase();
            const patient = (t.patientName || "").toLowerCase();
            const q = search.toLowerCase();
            return jobId.includes(q) || patient.includes(q);
        });

        list.sort((a, b) => {
            const ma = moment(a.fromDate);
            const mb = moment(b.fromDate);

            if (sortBy === "newest") return mb.valueOf() - ma.valueOf();
            if (sortBy === "oldest") return ma.valueOf() - mb.valueOf();
            if (sortBy === "atoz")
                return (a.patientName || "").localeCompare(b.patientName || "");
            if (sortBy === "ztoa")
                return (b.patientName || "").localeCompare(a.patientName || "");
            return 0;
        });

        return list;
    }, [transactions, search, sortBy]);

    const partnerName = partnerInfo?.name || "Partner";
    const partnerLocation = partnerInfo
        ? `${partnerInfo.city || ""}${partnerInfo.city && partnerInfo.state ? ", " : ""
        }${partnerInfo.state || ""}`
        : "";
    const partnerCode = partnerInfo?.referralCode || partnerId || "-";

    const handleToggleExpand = (jobID) => {
        setExpandedJobId((prev) => (prev === jobID ? null : jobID));
    };

    return (
        <div className="font-poppins bg-[#f3f5fb] rounded-2xl w-full h-[82vh] flex flex-col gap-4 overflow-y-auto p-2">
            {/* Back */}
            <button
                className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
                onClick={() => setSelectedPartner(null)}
            >
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back</span>
            </button>

            {/* Header card */}
            <div className="bg-white rounded-[10px] shadow-sm border border-slate-100 p-4 flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-20 h-12 rounded-[8px] overflow-hidden bg-slate-100 flex items-center justify-center text-[10px] font-semibold text-slate-500">
                        {coverImage ? (
                            <img
                                src={coverImage ? coverImage : partnerPortalImages.partnerDefaultDp}
                                alt={"LOGO"}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-[10px] font-semibold text-slate-500 font-poppins">
                                LOGO
                            </span>
                        )}
                    </div>
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="text-lg font-semibold text-slate-900">
                                {partnerName}
                            </h1>
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-600 text-xs px-2 py-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Active
                            </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                            <div className="flex items-center gap-1">
                                <PiHospitalThin size={14} />
                                <span>Partner</span>
                            </div>

                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                            <span>ID: {partnerCode}</span>
                            {partnerLocation && (
                                <>
                                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                                    <div className="flex items-center gap-1">
                                        <GrLocation size={14} />
                                        <span>{partnerLocation}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="text-right text-[11px] text-slate-500 mt-1">
                    <p>Onboarded</p>
                    <div className="flex items-center gap-1 justify-end">
                        <CiCalendar size={14} />
                        <p className="text-[12px] font-medium text-slate-900">
                            {createdAt ? moment(createdAt).format("DD-MMM-YYYY") : "-"}
                        </p>
                    </div>
                </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {statCards.map((card) => (
                    <StatCard
                        key={card.id}
                        label={card.label}
                        value={card.value}
                        icon={card.icon}
                        accentClasses={card.accentClasses}
                    />
                ))}
            </div>

            {/* Overview cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <OverviewCard
                    title="Bookings Overview"
                    right={
                        <span className="text-[11px] text-emerald-500 font-medium">
                            ↑
                        </span>
                    }
                >
                    <div className="text-sm">
                        <p className="text-slate-500 text-xs">Total bookings</p>
                        <p className="mt-1 text-xl font-semibold text-slate-900">
                            {metrics?.totalBooking ?? 0}
                        </p>
                    </div>
                    <div className="mt-3 text-xs text-slate-500">
                        Total HCPs{" "}
                        <span className="font-semibold text-slate-900 ml-1">
                            {metrics?.totalUsers ?? 0}
                        </span>
                    </div>
                </OverviewCard>

                <OverviewCard
                    title="Earnings Summary"
                    right={
                        <span className="text-[11px] text-slate-400 cursor-pointer">
                            i
                        </span>
                    }
                >
                    <div className="text-sm">
                        <p className="text-slate-500 text-xs">Total earnings</p>
                        <p className="mt-1 text-2xl font-semibold text-indigo-600">
                            {formatCurrency(metrics?.totalEarning)}
                        </p>
                    </div>
                    <div className="mt-3 text-xs text-slate-500">
                        Pending amount{" "}
                        <span className="font-semibold text-slate-900 ml-1">
                            {formatCurrency(metrics?.pendingAmount)}
                        </span>
                    </div>
                </OverviewCard>

                <OverviewCard
                    title="HCP Distribution"
                    right={
                        <span className="text-[11px] text-slate-400 cursor-pointer">
                            ⋮
                        </span>
                    }
                >
                    <div className="flex justify-between text-xs text-slate-600">
                        <div>
                            <p>With bookings</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">
                                {
                                    (hcpList || []).filter((h) => (h.totalBookings || 0) > 0)
                                        .length
                                }
                            </p>
                        </div>
                        <div className="text-right">
                            <p>Without bookings</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">
                                {
                                    (hcpList || []).filter((h) => (h.totalBookings || 0) === 0)
                                        .length
                                }
                            </p>
                        </div>
                    </div>
                    <div className="mt-3">
                        <div className="w-full h-1.5 bg-slate-100 rounded-full">
                            {(() => {
                                const total = hcpList.length || 1;
                                const withBookings = hcpList.filter(
                                    (h) => (h.totalBookings || 0) > 0
                                ).length;
                                const perc = Math.round((withBookings / total) * 100);
                                return (
                                    <div
                                        className="h-1.5 bg-indigo-500 rounded-full"
                                        style={{ width: `${perc}%` }}
                                    />
                                );
                            })()}
                        </div>
                        <p className="mt-1 text-[11px] text-slate-500">
                            HCPs with at least one booking
                        </p>
                    </div>
                </OverviewCard>
            </div>

            {/* Search + table / tabs */}
            <div className="flex flex-col flex-1 min-h-0 gap-3">
                <div className="flex flex-col md:flex-row gap-3 items-center">
                    <div className="relative flex-1 w-full">
                        <input
                            type="text"
                            placeholder="Search..."
                            className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <select
                        className="w-full md:w-44 rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-[12px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <option value="newest">Newest (by date)</option>
                        <option value="oldest">Oldest (by date)</option>
                        <option value="atoz">A to Z (by name)</option>
                        <option value="ztoa">Z to A (by name)</option>
                    </select>
                </div>

                {isExpanded && (
                    <div
                        className="fixed inset-0 bg-black/30 z-40"
                        onClick={() => setIsExpanded(false)}
                    />
                )}

                <div
                    className={
                        isExpanded
                            ? "fixed inset-6 z-50 bg-white rounded-[10px] shadow-lg border border-slate-100 flex flex-col"
                            : "bg-white rounded-[10px] shadow-sm border border-slate-100 flex flex-col flex-1 min-h-[50vh] overflow-y-auto"
                    }
                >
                    {/* Tabs header */}
                    <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex gap-2 sm:gap-3">
                            <TabButton
                                active={activeTab === "bookings"}
                                onClick={() => setActiveTab("bookings")}
                            >
                                Active Booking
                            </TabButton>
                            <TabButton
                                active={activeTab === "hcps"}
                                onClick={() => setActiveTab("hcps")}
                            >
                                List of HCPs
                            </TabButton>
                            <TabButton
                                active={activeTab === "transactions"}
                                onClick={() => setActiveTab("transactions")}
                            >
                                Transactions
                            </TabButton>
                        </div>

                        <button
                            onClick={() => setIsExpanded((prev) => !prev)}
                            className="p-1.5 rounded-full border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300 bg-white"
                            title={isExpanded ? "Shrink" : "Expand"}
                        >
                            {isExpanded ? <FiMinimize2 size={16} /> : <FiMaximize2 size={16} />}
                        </button>
                    </div>

                    {/* Content area */}
                    <div className="flex-1 overflow-y-auto">
                        {/* Bookings */}
                        {activeTab === "bookings" && (
                            <table className="min-w-full text-xs sm:text-sm">
                                <thead className="bg-slate-50 sticky top-0 z-10">
                                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                                        <th className="py-3 pl-4 pr-2 font-medium">Patient Name</th>
                                        <th className="px-2 font-medium">Service Type</th>
                                        <th className="px-2 font-medium">Visit ID</th>
                                        <th className="px-2 font-medium">Assigned HCP</th>
                                        <th className="px-2 font-medium">Date</th>
                                        <th className="px-2 font-medium">Status</th>
                                        <th className="px-4 font-medium text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBookings.map((row, idx) => (
                                        <tr
                                            key={row.visitID || idx}
                                            className="border-t border-slate-100 hover:bg-slate-50/60"
                                        >
                                            <td className="py-3 pl-4 pr-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-medium text-slate-600">
                                                        {(row.patientName || "")
                                                            .split(" ")
                                                            .map((n) => n[0])
                                                            .join("")}
                                                    </div>
                                                    <span className="text-slate-800">
                                                        {row.patientName}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-2 text-slate-600">
                                                {row.serviceType || "-"}
                                            </td>
                                            <td className="px-2 text-slate-600">
                                                {row.visitID || "-"}
                                            </td>
                                            <td className="px-2 text-indigo-600 cursor-default">
                                                {row.assignedHCP || "Unassigned"}
                                            </td>
                                            <td className="px-2 text-slate-600">
                                                {formatDate(row.date)}
                                            </td>
                                            <td className="px-2">
                                                <BookingStatusPill status={row.status} />
                                            </td>
                                            <td className="px-4 text-right font-semibold text-slate-900">
                                                {formatCurrency(row.amount)}
                                            </td>
                                        </tr>
                                    ))}

                                    {filteredBookings.length === 0 && !loading && (
                                        <tr>
                                            <td
                                                colSpan={7}
                                                className="py-6 text-center text-sm text-slate-500"
                                            >
                                                No bookings found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {/* HCPs */}
                        {activeTab === "hcps" && (
                            <table className="min-w-full text-xs sm:text-sm">
                                <thead className="bg-slate-50 sticky top-0 z-10">
                                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                                        <th className="py-3 pl-4 pr-2 font-medium">HCP Name</th>
                                        <th className="px-2 font-medium">Role</th>
                                        <th className="px-2 font-medium">Status</th>
                                        <th className="px-2 font-medium text-right">
                                            Total Bookings
                                        </th>
                                        <th className="px-4 font-medium text-right">
                                            Total Earnings
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredHcps.map((hcp, idx) => (
                                        <tr
                                            key={hcp.name || idx}
                                            className="border-t border-slate-100 hover:bg-slate-50/60"
                                        >
                                            <td className="py-3 pl-4 pr-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-medium text-slate-600">
                                                        {(hcp.name || "")
                                                            .split(" ")
                                                            .map((n) => n[0])
                                                            .join("")}
                                                    </div>
                                                    <span className="text-indigo-600 cursor-default">
                                                        {hcp.name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-2 text-slate-600">
                                                {hcp.role || "-"}
                                            </td>
                                            <td className="px-2">
                                                <HcpStatusPill status={hcp.active ? "Active" : "Inactive"} />
                                            </td>
                                            <td className="px-2 text-right text-slate-700">
                                                {hcp.totalBookings ?? 0}
                                            </td>
                                            <td className="px-4 text-right font-semibold text-slate-900">
                                                {formatCurrency(0)}
                                            </td>
                                        </tr>
                                    ))}

                                    {filteredHcps.length === 0 && !loading && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="py-6 text-center text-sm text-slate-500"
                                            >
                                                No HCPs found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {/* Transactions – expandable cards */}
                        {activeTab === "transactions" && (
                            <div className="p-3 space-y-3">
                                {filteredTxns.map((tx, idx) => {
                                    const statusLabel = getTxnStatusLabel(tx.status);
                                    const typeLabel = getTxnTypeLabel(tx.status);
                                    const isExpandedRow = expandedJobId === tx.jobID;

                                    const dutyTxs = tx.dutyTransactions || [];
                                    const totalDays = tx.days || dutyTxs.length || 0;

                                    const totalDailyFee = dutyTxs.reduce(
                                        (sum, d) => sum + (d.dailyFee || 0),
                                        0
                                    );
                                    const totalPenalty = dutyTxs.reduce(
                                        (sum, d) => sum + (d.penalty || 0),
                                        0
                                    );
                                    const totalPlatformFee = 0; // not provided
                                    const totalAmount = dutyTxs.reduce(
                                        (sum, d) => sum + (d.amountToTransfer || 0),
                                        0
                                    );

                                    return (
                                        <div
                                            key={tx.jobID || idx}
                                            className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                                        >
                                            {/* Header row */}
                                            <div
                                                className="px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3 cursor-pointer hover:bg-slate-50"
                                                onClick={() => handleToggleExpand(tx.jobID)}
                                            >
                                                {/* Left: job + patient */}
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[12px] font-semibold text-slate-900">
                                                            {tx.jobID || "-"}
                                                        </span>
                                                        <TxnStatusPill status={statusLabel} />
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-medium text-slate-600">
                                                            {(tx.patientName || "")
                                                                .split(" ")
                                                                .map((n) => n[0])
                                                                .join("")}
                                                        </div>
                                                        <div>
                                                            <p className="text-[12px] font-medium text-slate-900">
                                                                {tx.patientName}
                                                            </p>
                                                            <p className="text-[11px] text-slate-500">
                                                                {tx.visitAddress || "-"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right: schedule summary */}
                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px] text-slate-500 md:text-right">
                                                    <div>
                                                        <p>Scheduled Date</p>
                                                        <p className="mt-1 text-[12px] font-medium text-slate-900">
                                                            {formatDate(tx.fromDate)}{" "}
                                                            {tx.toDate &&
                                                                tx.toDate !== tx.fromDate &&
                                                                `- ${formatDate(tx.toDate)}`}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p>Time</p>
                                                        <p className="mt-1 text-[12px] font-medium text-slate-900">
                                                            {tx.time || "-"}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p>Total Days</p>
                                                        <p className="mt-1 text-[12px] font-medium text-slate-900">
                                                            {totalDays} day{totalDays === 1 ? "" : "s"}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p>Assigned HCP</p>
                                                        <p className="mt-1 text-[12px] font-medium text-slate-900">
                                                            {tx.specialistName || "-"}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* View/Hide breakdown control */}
                                                <div className="flex items-center gap-1 text-[11px] text-indigo-600 font-medium">
                                                    <span>
                                                        {isExpandedRow ? "Hide breakdown" : "View breakdown"}
                                                    </span>
                                                    {isExpandedRow ? (
                                                        <FiChevronUp size={12} />
                                                    ) : (
                                                        <FiChevronDown size={12} />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Expanded breakdown */}
                                            {isExpandedRow && (
                                                <div className="border-t border-slate-200 bg-slate-50/60 px-4 pb-3 pt-2">
                                                    {dutyTxs.length === 0 ? (
                                                        <div className="py-4 text-[12px] text-slate-500">
                                                            No duty transactions available for this job.
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <table className="w-full text-[11px] sm:text-[12px] bg-white rounded-[12px] overflow-hidden">
                                                                <thead className="bg-slate-100">
                                                                    <tr className="text-slate-500">
                                                                        <th className="py-2 pl-3 pr-1 text-left font-medium">
                                                                            Day
                                                                        </th>
                                                                        <th className="px-2 text-left font-medium">
                                                                            Attended By
                                                                        </th>
                                                                        <th className="px-2 text-right font-medium">
                                                                            Daily Fee
                                                                        </th>
                                                                        <th className="px-2 text-right font-medium">
                                                                            Penalty
                                                                        </th>
                                                                        <th className="px-2 text-right font-medium">
                                                                            Platform Fee
                                                                        </th>
                                                                        <th className="px-2 text-right font-medium">
                                                                            Amount
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {dutyTxs.map((d, i) => {
                                                                        const dayLabel = d.day || `${i + 1}/${totalDays}`;
                                                                        const dutyDate = moment(tx.fromDate)
                                                                            .add(i, "days")
                                                                            .format("DD MMM YYYY, hh:mm A");

                                                                        return (
                                                                            <tr
                                                                                key={dayLabel + i}
                                                                                className="border-t border-slate-100"
                                                                            >
                                                                                <td className="py-2 pl-3 pr-1 align-top">
                                                                                    <div className="text-[12px] font-medium text-slate-900">
                                                                                        Day {dayLabel}
                                                                                    </div>
                                                                                    <div className="text-[11px] text-slate-500">
                                                                                        {dutyDate}
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-2 align-top text-slate-700">
                                                                                    {d.attendedBy || "-"}
                                                                                </td>
                                                                                <td className="px-2 align-top text-right text-slate-700">
                                                                                    {formatCurrency(d.dailyFee)}
                                                                                </td>
                                                                                <td className="px-2 align-top text-right text-slate-700">
                                                                                    {formatCurrency(d.penalty)}
                                                                                </td>
                                                                                <td className="px-2 align-top text-right text-slate-700">
                                                                                    {formatCurrency(0)}
                                                                                </td>
                                                                                <td className="px-2 align-top text-right font-semibold text-slate-900">
                                                                                    {formatCurrency(d.amountToTransfer)}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}

                                                                    {/* Totals row */}
                                                                    <tr className="bg-slate-50 border-t border-slate-200">
                                                                        <td className="py-2 pl-3 pr-1 text-[12px] font-semibold text-slate-900">
                                                                            Total Days{" "}
                                                                            <span className="font-normal text-slate-600">
                                                                                ({totalDays})
                                                                            </span>
                                                                        </td>
                                                                        <td />
                                                                        <td className="px-2 text-right text-[12px] text-slate-700">
                                                                            {formatCurrency(totalDailyFee)}
                                                                        </td>
                                                                        <td className="px-2 text-right text-[12px] text-slate-700">
                                                                            {formatCurrency(totalPenalty)}
                                                                        </td>
                                                                        <td className="px-2 text-right text-[12px] text-slate-700">
                                                                            {formatCurrency(totalPlatformFee)}
                                                                        </td>
                                                                        <td className="px-2 text-right text-[12px] font-semibold text-slate-900">
                                                                            {formatCurrency(totalAmount)}
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>

                                                            <div className="mt-2 text-[12px] font-semibold text-slate-900">
                                                                Total Amount for this Duty:{" "}
                                                                <span className="text-indigo-600">
                                                                    {formatCurrency(totalAmount)}
                                                                </span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {filteredTxns.length === 0 && !loading && (
                                    <p className="py-6 text-center text-sm text-slate-500">
                                        No transactions found.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PartnerAnalysis;